import type { SessionId, UserId } from '@/schemas';
import { handleBackgroundError } from '../error-handler';
import { isBlockedError } from './error-utils';
import { syncGameDefinitions } from './game-definitions';
import { syncImages } from './images';
import { mergeUnauthenticatedContent } from './merge-unauthenticated';
import { syncPlayerEvents } from './player-events';
import { syncPlayerGroups } from './player-groups';
import { syncSessionIndex, type SyncSessionIndexResult } from './session-index';
import { syncTeamPresets } from './team-presets';
import { syncSession } from './index';

export interface SyncAllResult {
  sessions: { uploadedCount: number; downloadedCount: number };
  playerEvents: { uploadedCount: number; downloadedCount: number };
  gameDefinitions: { uploadedCount: number; downloadedCount: number };
  teamPresets: { uploadedCount: number; downloadedCount: number };
  playerGroups: { uploadedCount: number; downloadedCount: number };
  images: { uploadedCount: number; downloadedCount: number };
}

interface SyncResult {
  uploadedCount: number;
  downloadedCount: number;
}

/**
 * Handle sync error with logging and default value return
 *
 * @remarks
 * This function uses a return type pattern where errors return a default value
 * to allow sync operations to continue even if individual operations fail.
 * This ensures that partial sync failures don't prevent other operations from completing.
 *
 * @param syncFn - Sync function to execute
 * @param entityName - Name of entity being synced (for logging)
 * @param defaultValue - Default value to return on error
 * @param useErrorLog - Whether to use console.error (true) or console.warn (false)
 * @returns Promise resolving to sync result or default value (never throws)
 */
function handleSyncError<T extends SyncResult>(
  syncFn: () => Promise<T>,
  entityName: string,
  defaultValue: T,
  useErrorLog = false,
): Promise<T> {
  return syncFn().catch((error: unknown) => {
    handleBackgroundError(error, { operation: 'sync', entityName }, useErrorLog ? 'error' : 'warn');

    if (isBlockedError(error)) {
      const logFn = useErrorLog ? console.error : console.warn;
      logFn(
        '⚠️ Content blocker detected! Please disable browser extensions blocking Supabase or whitelist *.supabase.co',
      );
    }

    return defaultValue;
  });
}

/**
 * Handle session index sync error with logging and default value return
 *
 * @remarks
 * This function uses a return type pattern where errors return a default value
 * to allow sync operations to continue even if the session index sync fails.
 * Returns an empty array for downloaded sessions on error.
 *
 * @param syncFn - Sync function to execute
 * @param useErrorLog - Whether to use console.error (true) or console.warn (false)
 * @returns Promise resolving to sync result with empty downloadedSessions array on error (never throws)
 */
function handleSessionIndexSyncError(
  syncFn: () => Promise<SyncSessionIndexResult>,
  useErrorLog = false,
): Promise<SyncSessionIndexResult> {
  return syncFn().catch((error: unknown) => {
    handleBackgroundError(error, { operation: 'syncSessionIndex' }, useErrorLog ? 'error' : 'warn');

    if (isBlockedError(error)) {
      const logFn = useErrorLog ? console.error : console.warn;
      logFn(
        '⚠️ Content blocker detected! Please disable browser extensions blocking Supabase or whitelist *.supabase.co',
      );
    }

    return { downloadedSessions: [] };
  });
}

/**
 * Sync all user's data (called on authentication)
 */
export async function syncUserData(userId: UserId): Promise<SyncAllResult> {
  if (!userId) {
    throw new Error('userId is required for syncUserData');
  }
  // First, merge any unauthenticated content to the authenticated user
  await mergeUnauthenticatedContent(userId);

  // Sync all entity types in parallel
  const defaultSyncResult: SyncResult = { uploadedCount: 0, downloadedCount: 0 };
  const syncResults = await Promise.all([
    handleSyncError(() => syncPlayerEvents(userId), 'player events', defaultSyncResult, true),
    handleSyncError(() => syncGameDefinitions(userId), 'game definitions', defaultSyncResult, true),
    handleSyncError(() => syncTeamPresets(userId), 'team presets', defaultSyncResult, true),
    handleSyncError(() => syncPlayerGroups(userId), 'player groups', defaultSyncResult, true),
    handleSyncError(() => syncImages(userId), 'images', defaultSyncResult, true),
    handleSessionIndexSyncError(() => syncSessionIndex(userId), true),
  ]);
  const [playerEvents, gameDefinitions, teamPresets, playerGroups, images] = syncResults.slice(
    0,
    5,
  ) as [SyncResult, SyncResult, SyncResult, SyncResult, SyncResult];

  // Sync all user's sessions
  // First, get all session IDs from local metadata (after session index sync)
  const { db } = await import('@/db/client');
  const sessionMetadata = await db.sessionMetadata.filter((meta) => !meta.deletedAt).toArray();

  const sessionResults = await Promise.allSettled(
    sessionMetadata.map((meta) => syncSession(meta.id)),
  );

  const sessionsResult: { uploadedCount: number; downloadedCount: number } = {
    uploadedCount: 0,
    downloadedCount: 0,
  };
  for (const result of sessionResults) {
    if (result.status === 'fulfilled') {
      sessionsResult.uploadedCount += result.value.uploadedCount;
      sessionsResult.downloadedCount += result.value.downloadedCount;
    }
  }

  return {
    sessions: sessionsResult,
    playerEvents,
    gameDefinitions,
    teamPresets,
    playerGroups,
    images,
  };
}

/**
 * Sync all entities (for periodic sync)
 */
export async function syncAll(userId: UserId, sessionIds: SessionId[]): Promise<SyncAllResult> {
  const sessionIndexResult = await handleSessionIndexSyncError(
    () => syncSessionIndex(userId),
    false,
  );

  const sessionIdSet = new Set<SessionId>(sessionIds);
  for (const sessionId of sessionIndexResult.downloadedSessions) {
    sessionIdSet.add(sessionId);
  }
  const effectiveSessionIds = [...sessionIdSet];

  // Sync all entity types in parallel
  const defaultSyncResult: SyncResult = { uploadedCount: 0, downloadedCount: 0 };
  const [playerEvents, gameDefinitions, teamPresets, playerGroups, images, ...sessionResults] =
    await Promise.all([
      handleSyncError(() => syncPlayerEvents(userId), 'player events', defaultSyncResult, false),
      handleSyncError(
        () => syncGameDefinitions(userId),
        'game definitions',
        defaultSyncResult,
        false,
      ),
      handleSyncError(() => syncTeamPresets(userId), 'team presets', defaultSyncResult, false),
      handleSyncError(() => syncPlayerGroups(userId), 'player groups', defaultSyncResult, false),
      handleSyncError(() => syncImages(userId), 'images', defaultSyncResult, false),
      ...effectiveSessionIds.map((sessionId) =>
        handleSyncError(
          () => syncSession(sessionId),
          `session ${sessionId}`,
          defaultSyncResult,
          false,
        ),
      ),
    ]);

  const sessionsResult: { uploadedCount: number; downloadedCount: number } = {
    uploadedCount: 0,
    downloadedCount: 0,
  };
  for (const result of sessionResults) {
    sessionsResult.uploadedCount += result.uploadedCount;
    sessionsResult.downloadedCount += result.downloadedCount;
  }

  return {
    sessions: sessionsResult,
    playerEvents,
    gameDefinitions,
    teamPresets,
    playerGroups,
    images,
  };
}
